import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, CircularProgress } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  HomeWork as HomeWorkIcon,
  TaskAlt as TaskAltIcon,
  Storefront as StorefrontIcon,
  Public as PublicIcon,
  Logout as LogoutIcon,
  PeopleAlt as PeopleAltIcon,
  Apartment as ApartmentIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  PersonOff as PersonOffIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../../Contexts/AuthContext';
import { authAPI } from '../../services/api';
import UserManagement from '../UserManagement/UserManagement';
import { PropertiesUnderVerification } from '../PropertyDetails/PropertiesUnderVerification';
import BrokerManagement from '../BrokerManagement/BrokerManagement';
import './AdminPortal.scss';

const adminMenuItems = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    helperText: 'Overview and analytics',
    path: '/admin-portal/dashboard',
    icon: DashboardIcon,
  },
  {
    key: 'users',
    label: 'Manage Users',
    helperText: 'User accounts and roles',
    path: '/admin-portal/users',
    icon: GroupIcon,
  },
  {
    key: 'listings',
    label: 'Manage Listings',
    helperText: 'All property listings',
    path: '/admin-portal/listings',
    icon: HomeWorkIcon,
  },
  {
    key: 'verification',
    label: 'Verification',
    helperText: 'Review and approve listings',
    path: '/admin-portal/listings',
    icon: TaskAltIcon,
  },
  // {
  //   key: 'admin-fields',
  //   label: 'Admin Fields',
  //   helperText: 'Availability and checks',
  //   path: '/users',
  //   icon: RuleIcon,
  // },
  {
    key: 'broker-mgmt',
    label: 'Broker Mgmt',
    helperText: 'Broker profiles',
    path: '/admin-portal/brokers',
    icon: StorefrontIcon,
  },
];

const districtRows = [
  { label: 'Bangalore Urban', value: 100 },
  { label: 'Mandya', value: 34 },
  { label: 'Dakshina Kannada', value: 34 },
  { label: 'Dharwad', value: 34 },
];

const initialDashboardData = {
  totalProperties: 0,
  activeUsers: 0,
  inactiveUsers: 0,
  buyers: 0,
  owners: 0,
  brokers: 0,
  availableProperties: 0,
  pendingVerification: 0,
  draftProperties: 0,
  recentUsers: [],
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const mapRecentUser = (user) => {
  const firstName = String(user?.firstName || user?.first_name || '').trim();
  const lastName = String(user?.lastName || user?.last_name || '').trim();
  const name = `${firstName} ${lastName}`.trim() || user?.name || user?.user_name || 'Unknown User';
  const email = user?.email || user?.email_id || '--';
  const role = user?.role || user?.userType || user?.user_type || 'User';

  return {
    id: user?.id || user?.userId || email,
    name,
    email,
    tag: String(role),
  };
};

export function AdminPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, userProfile } = useAuth();
  const [dashboardData, setDashboardData] = useState(initialDashboardData);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const profileSource = user || userProfile || {};
  const firstName = String(profileSource?.firstName || profileSource?.first_name || '').trim();
  const lastName = String(profileSource?.lastName || profileSource?.last_name || '').trim();
  const fullName = `${firstName} ${lastName}`.trim() || profileSource?.name || profileSource?.fullName || 'Admin User';
  const email = profileSource?.email || profileSource?.email_id || 'admin@vruddhirealty.com';
  const roleValue = profileSource?.role_name || profileSource?.role || profileSource?.userType || profileSource?.user_type;
  const roleId = Number(profileSource?.role_id ?? profileSource?.roleId);
  const roleLabel = String(roleValue || (roleId === 1 ? 'Super Admin' : 'Admin')).trim();
  const avatarText = fullName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'A';

  const activeMenuKey = useMemo(() => {
    if (location.pathname.startsWith('/admin-portal/brokers')) return 'broker-mgmt';
    if (location.pathname.startsWith('/admin-portal/users') || location.pathname.startsWith('/users')) return 'users';
    if (location.pathname.startsWith('/admin-portal/listings')) return 'listings';
    if (location.pathname.startsWith('/all-properties')) return 'listings';
    return 'dashboard';
  }, [location.pathname]);

  const isUsersScreen = location.pathname.startsWith('/admin-portal/users');
  const isListingsScreen = location.pathname.startsWith('/admin-portal/listings');
  const isBrokerScreen = location.pathname.startsWith('/admin-portal/brokers');

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isUsersScreen || isListingsScreen || isBrokerScreen) {
      return;
    }

    const fetchDashboardData = async () => {
      try {
        setDashboardLoading(true);
        setDashboardError('');
        const response = await authAPI.getAdminDashboard();
        const payload = response?.data?.data || {};

        setDashboardData({
          totalProperties: toNumber(payload?.totalProperties),
          activeUsers: toNumber(payload?.activeUsers),
          inactiveUsers: toNumber(payload?.inactiveUsers),
          buyers: toNumber(payload?.buyers),
          owners: toNumber(payload?.owners),
          brokers: toNumber(payload?.brokers),
          availableProperties: toNumber(payload?.availableProperties),
          pendingVerification: toNumber(payload?.pendingVerification),
          draftProperties: toNumber(payload?.draftProperties),
          recentUsers: Array.isArray(payload?.recentUsers)
            ? payload.recentUsers.map(mapRecentUser)
            : [],
        });
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        setDashboardError(apiMessage || 'Unable to load dashboard details.');
      } finally {
        setDashboardLoading(false);
      }
    };

    fetchDashboardData();
  }, [isUsersScreen, isListingsScreen, isBrokerScreen]);

  const statCards = useMemo(() => {
    const derivedTotalListings = dashboardData.availableProperties
      + dashboardData.pendingVerification
      + dashboardData.draftProperties;
    const totalListings = dashboardData.totalProperties > 0
      ? dashboardData.totalProperties
      : derivedTotalListings;

    return [
      {
        key: 'active-users',
        icon: PeopleAltIcon,
        value: dashboardData.activeUsers,
        title: 'Total Active Users',
        hint: `${dashboardData.buyers} buyers • ${dashboardData.owners} owners`,
        tone: 'blue',
      },
      {
        key: 'total-listings',
        icon: ApartmentIcon,
        value: totalListings,
        title: 'Total Listings',
        hint: `${dashboardData.availableProperties} live • ${dashboardData.pendingVerification} pending`,
        tone: 'violet',
      },
      {
        key: 'verified-listings',
        icon: VerifiedIcon,
        value: dashboardData.availableProperties,
        title: 'Verified Listings',
        hint: 'Visible to public',
        tone: 'green',
      },
      {
        key: 'pending-verification',
        icon: ScheduleIcon,
        value: dashboardData.pendingVerification,
        title: 'Pending Verification',
        hint: 'Awaiting review',
        tone: 'orange',
      },
      {
        key: 'broker-users',
        icon: StarIcon,
        value: dashboardData.brokers,
        title: 'Broker Users',
        hint: '',
        tone: 'purple',
      },
      {
        key: 'owners',
        icon: PersonIcon,
        value: dashboardData.owners,
        title: 'Owners',
        hint: '',
        tone: 'teal',
      },
      {
        key: 'draft-properties',
        icon: VisibilityIcon,
        value: dashboardData.draftProperties,
        title: 'Draft Properties',
        hint: '',
        tone: 'slate',
      },
      {
        key: 'inactive-users',
        icon: PersonOffIcon,
        value: dashboardData.inactiveUsers,
        title: 'Inactive Users',
        hint: '',
        tone: 'red',
      },
    ];
  }, [dashboardData]);

  const totalListingsCount = dashboardData.totalProperties > 0
    ? dashboardData.totalProperties
    : (dashboardData.availableProperties + dashboardData.pendingVerification + dashboardData.draftProperties);

  let headerBreadcrumb = 'Admin > Dashboard';
  if (isUsersScreen) {
    headerBreadcrumb = 'Admin > Manage Users';
  } else if (isBrokerScreen) {
    headerBreadcrumb = 'Admin > Broker Mgmt';
  } else if (isListingsScreen) {
    headerBreadcrumb = 'Admin > Manage Listings';
  }

  let headerTitle = 'Overview & analytics';
  if (isUsersScreen) {
    headerTitle = 'User accounts & roles';
  } else if (isBrokerScreen) {
    headerTitle = 'Broker profiles';
  } else if (isListingsScreen) {
    headerTitle = 'All property listings';
  }

  const handleMenuClick = (item) => {
    if (item.key === 'broker-mgmt') {
      navigate('/admin-portal/brokers');
      setIsMobileMenuOpen(false);
      return;
    }
    navigate(item.path);
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  const handleViewPublicSite = () => {
    sessionStorage.removeItem('propertiesCache');
    navigate('/', { state: { homeRefreshAt: Date.now() } });
  };

  const handleOpenMobileMenu = () => {
    setIsMobileMenuOpen(true);
  };

  const handleCloseMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={`admin-portal-shell ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
      <button
        type="button"
        className="portal-mobile-backdrop"
        aria-label="Close navigation menu"
        onClick={handleCloseMobileMenu}
      />

      <aside className={`admin-portal-sidebar ${isMobileMenuOpen ? 'is-open' : ''}`}>
        <div className="portal-brand">
          <button
            type="button"
            className="portal-mobile-close"
            aria-label="Close menu"
            onClick={handleCloseMobileMenu}
          >
            <CloseIcon fontSize="small" />
          </button>
          <h2>Vruddhi Realty</h2>
          <p>Admin Portal</p>
        </div>

        <div className="portal-user">
          <span className="avatar">{avatarText}</span>
          <div>
            <h3>{fullName}</h3>
            <p>{email}</p>
            <span className="role-pill">{roleLabel}</span>
          </div>
        </div>

        <nav className="portal-nav">
          {adminMenuItems.map((item) => {
            const isActive = item.key === activeMenuKey;
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                className={`portal-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => handleMenuClick(item)}
              >
                <Icon fontSize="small" />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.helperText}</small>
                </span>
              </button>
            );
          })}
        </nav>

        <div className="portal-footer-actions">
          <button type="button" onClick={handleViewPublicSite}>
            <PublicIcon fontSize="small" />
            View Public Site
          </button>
          <button type="button" onClick={handleSignOut}>
            <LogoutIcon fontSize="small" />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="admin-portal-main">
        <div className="portal-mobile-topbar">
          <button
            type="button"
            className="portal-mobile-menu-trigger"
            aria-label="Open navigation menu"
            onClick={handleOpenMobileMenu}
          >
            <MenuIcon fontSize="small" />
          </button>
          <strong>Admin Portal</strong>
        </div>

        <header className="portal-main-header">
          <div>
            <p>{headerBreadcrumb}</p>
            <h1>{headerTitle}</h1>
          </div>
          {!isUsersScreen && !isListingsScreen && !isBrokerScreen && (
            <div className="header-tags">
              <span>{dashboardData.pendingVerification} need check</span>
              <span>{dashboardData.activeUsers + dashboardData.inactiveUsers} users</span>
              <span>{totalListingsCount} listings</span>
            </div>
          )}
        </header>

        {!isUsersScreen && !isListingsScreen && !isBrokerScreen && (
          <section className="portal-intro">
            <h2>Dashboard Overview</h2>
            <p>Vruddhi Realty - Karnataka Property Portal</p>
          </section>
        )}

        {isUsersScreen ? (
          <section className="admin-portal-embedded-screen">
            <UserManagement />
          </section>
        ) : isListingsScreen ? (
          <section className="admin-portal-embedded-screen">
            <PropertiesUnderVerification embeddedInAdminPortal />
          </section>
        ) : isBrokerScreen ? (
          <section className="admin-portal-embedded-screen">
            <BrokerManagement />
          </section>
        ) : (
          <>
            {dashboardError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {dashboardError}
              </Alert>
            )}

            {dashboardLoading && (
              <section className="admin-portal-loading-state">
                <CircularProgress size={28} />
              </section>
            )}

            <section className="metrics-grid">
              {statCards.map((card) => {
                const Icon = card.icon;
                return (
                  <article key={card.key} className="metric-card">
                    <div className={`metric-icon ${card.tone}`}>
                      <Icon fontSize="small" />
                    </div>
                    <div>
                      <h3>{card.value}</h3>
                      <p>{card.title}</p>
                      <small>{card.hint}</small>
                    </div>
                  </article>
                );
              })}
            </section>

            <section className="chart-grid">
              <article className="chart-card wide">
                <h3>Listings by Status</h3>
                <div className="placeholder-chart">
                  <span>Listed: {dashboardData.availableProperties}</span>
                  <span>Pending: {dashboardData.pendingVerification}</span>
                  <span>Draft: {dashboardData.draftProperties}</span>
                </div>
              </article>

              <article className="chart-card">
                <h3>User Distribution</h3>
                <div className="donut-wrap">
                  <div className="donut" />
                  <ul>
                    <li><i className="blue" /> Buyers: {dashboardData.buyers}</li>
                    <li><i className="green" /> Owners: {dashboardData.owners}</li>
                    <li><i className="red" /> Brokers: {dashboardData.brokers}</li>
                  </ul>
                </div>
              </article>
            </section>

            <section className="chart-card district-card">
              <h3>Properties by District</h3>
              <div className="district-bars">
                {districtRows.map((item) => (
                  <div key={item.label} className="district-row">
                    <span>{item.label}</span>
                    <div className="bar-track">
                      <div className="bar-fill" style={{ width: `${item.value}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="chart-card recent-users-card">
              <h3>Recent Users</h3>
              {dashboardData.recentUsers.length === 0 ? (
                <div className="recent-user-row">
                  <div className="recent-user-profile">
                    <span>--</span>
                    <div>
                      <strong>No recent users</strong>
                      <p>Users created recently will appear here.</p>
                    </div>
                  </div>
                </div>
              ) : (
                dashboardData.recentUsers.map((user) => (
                  <div key={user.id} className="recent-user-row">
                    <div className="recent-user-profile">
                      <span>
                        {String(user.name || '')
                          .split(' ')
                          .filter(Boolean)
                          .map((part) => part[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase() || '--'}
                      </span>
                      <div>
                        <strong>{user.name}</strong>
                        <p>{user.email}</p>
                      </div>
                    </div>
                    <em>{user.tag}</em>
                  </div>
                ))
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export default AdminPortal;
